import CakeIcon from "@mui/icons-material/Cake";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import GppGoodIcon from "@mui/icons-material/GppGood";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import NearMeIcon from "@mui/icons-material/NearMe";
import PhoneIcon from "@mui/icons-material/Phone";
import { Box, IconButton, SvgIconProps, Typography } from "@mui/material";
import { formatDisplayPhoneNumber, useTranslation } from "hhn-commons";
import React, { useMemo } from "react";
import { IGroupInfo } from "@home-health-notify/domain";
import { sortBy } from "lodash";

function PatientInfoItem({
  info,
  icon,
}: {
  info: React.ReactNode;
  icon: React.ReactNode;
}) {
  const clonedIcon = React.isValidElement(icon)
    ? React.cloneElement(icon as React.ReactElement<SvgIconProps>, {
        color: "primary",
      })
    : icon;
  return (
    <Box
      sx={{
        marginTop: "10px",
        display: "flex",
        alignItems: "center",
        gap: "10px",
      }}
    >
      {clonedIcon}
      <Typography variant="subtitle1" component="span">
        {info}
      </Typography>
    </Box>
  );
}

export const PatientDetails = ({ groupInfo }: { groupInfo: IGroupInfo }) => {
  const { t } = useTranslation();
  const tNs = (key: string) => t(key, { ns: "groupChatInfo" });

  function getDate(timeStamp: number) {
    const date = new Date(timeStamp);
    return date.toLocaleDateString("en-US");
  }

  const sortedCaregivers = useMemo(
    () => sortBy(groupInfo.info?.careGiver ?? [], ["sortOrder"]),
    [groupInfo.info?.careGiver],
  );

  const primaryCaregiver = sortedCaregivers[0];

  const patientPhone = formatDisplayPhoneNumber(
    groupInfo.patientInfo!.countryCode,
    groupInfo.patientInfo!.phoneNo,
  );

  const caregiverPhone =
    primaryCaregiver?.phoneNo &&
    formatDisplayPhoneNumber(
      primaryCaregiver.countryCode,
      primaryCaregiver.phoneNo,
    );
  const caregiverName = primaryCaregiver?.name;
  return (
    <Box sx={{ padding: "20px" }}>
      <Typography sx={{ marginBottom: "20px" }}>
        {tNs("patientDetails")}
      </Typography>
      <PatientInfoItem
        info={`${tNs("dateOfBirth")} ${groupInfo.patientInfo!.dob} (${
          groupInfo.patientInfo!.age
        } ${groupInfo.patientInfo!.age === 1 ? "yr" : "yrs"})`}
        icon={<CakeIcon />}
      />

      {caregiverPhone ? (
        <>
          <PatientInfoItem
            icon={<PhoneIcon />}
            info={
              <>
                {patientPhone}
              </>
            }
          />
          <PatientInfoItem
            icon={<PhoneIcon />}
            info={
              <>
                {caregiverPhone}
                <Typography
                  component="span"
                  sx={{ color: "text.secondary", ml: 1 }}
                >
                  ( {caregiverName} )
                </Typography>
              </>
            }
          />
        </>
      ) : (
        <PatientInfoItem info={patientPhone} icon={<PhoneIcon />} />
      )}

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <PatientInfoItem
          info={`${
            groupInfo.patientInfo?.address?.apartmentNo
              ? `APT # ${groupInfo.patientInfo.address.apartmentNo}, `
              : ""
          }${groupInfo.patientInfo?.address?.placeName}`}
          icon={<LocationOnIcon />}
        />
        <IconButton
          href={`https://www.google.com/maps?q=${groupInfo.patientInfo?.address?.lat},${groupInfo.patientInfo?.address?.lng}`}
          aria-label="launch"
          color="secondary"
          target="_blank"
        >
          <NearMeIcon sx={{ fontSize: "large" }} />
        </IconButton>
      </Box>
      {groupInfo.patientCareTime && (
        <PatientInfoItem
          info={`${tNs("episode")} ${getDate(groupInfo.patientCareTime.startDate)} -
            ${getDate(groupInfo.patientCareTime.endDate)}`}
          icon={<CalendarTodayIcon />}
        />
      )}
      <PatientInfoItem
        info={`${tNs("insurance")} ${groupInfo.insurance ? groupInfo.insurance : "N/A"}`}
        icon={<GppGoodIcon />}
      />
    </Box>
  );
};
